import clsx from 'clsx';
import { FC, useState } from 'react';
import { useIntl } from 'react-intl';
import { Link } from 'react-router-dom';

import { PetModel } from '../../pets/models/PetModel';
import { DraftState } from '../hooks/useDraft';
import { QuickSalePlanModel } from '../models/QuickSaleModel';
import {
  ALL_FILTER,
  CURRENT_FILTER,
  CURRENT_PLAN_STATES,
} from '../utils/quickSaleFilters';
import { formatEuro } from '../utils/quickSaleFormat';
import { PlanKindModal } from './PlanKindModal';
import { PriceBreakdown } from './PriceBreakdown';
import { QuickSaleCard } from './QuickSaleCard';
import { StateBadge } from './StateBadge';
import { VariantsModal } from './VariantsModal';

type Props = {
  userId: number;
  pet: PetModel;
  plans: QuickSalePlanModel[];
  allPetPlans: QuickSalePlanModel[];
  selectedPlan: QuickSalePlanModel | null;
  viewPlan: QuickSalePlanModel | null;
  filter: string;
  onFilterChange: (filter: string) => void;
  onSelectPlan: (planId: number) => void;
  onCreatePlan: () => void;
  creatingPlan: boolean;
  customEditorOpen: boolean;
  onCustomEditorOpenChange: (open: boolean) => void;
  draftState: DraftState;
};

const PlansCard: FC<Props> = ({
  userId,
  pet,
  plans,
  allPetPlans,
  selectedPlan,
  viewPlan,
  filter,
  onFilterChange,
  onSelectPlan,
  onCreatePlan,
  creatingPlan,
  customEditorOpen,
  onCustomEditorOpenChange,
  draftState,
}) => {
  const intl = useIntl();
  const [variantsOpen, setVariantsOpen] = useState(false);
  const { draft, update, clear, simulating } = draftState;
  const planDraft = draft.plan || {};
  const states = Array.from(new Set(allPetPlans.map((plan) => plan.state)));
  const hasCurrentPlan = allPetPlans.some((plan) =>
    CURRENT_PLAN_STATES.includes(plan.state)
  );

  function onKindChange(kind: 'automatic' | 'custom') {
    if (!selectedPlan) {
      return;
    }
    if (kind === 'custom') {
      onCustomEditorOpenChange(true);
      return;
    }
    update('plan', {
      kind: selectedPlan.kind === 'automatic' ? undefined : 'automatic',
      custom_lines: undefined,
      allow_800gr: undefined,
    });
  }

  function onPeriodChange(periodId: number) {
    update('plan', {
      period: periodId === selectedPlan?.period?.id ? undefined : periodId,
    });
  }

  function onCombineChange(combine: boolean) {
    update('plan', {
      combine: combine === selectedPlan?.combine ? undefined : combine,
    });
  }

  function renderSelectedRow(plan: QuickSalePlanModel) {
    const kind = planDraft.kind ?? plan.kind;
    const periodId = planDraft.period ?? plan.period?.id;
    const combine = planDraft.combine ?? plan.combine;
    const editable = plan.is_detailed;

    return (
      <tr key={plan.id} className="bg-light-primary">
        <td className="ps-4 fw-bolder">
          {pet.name}
          {!!pet.is_priority_pet && (
            <i
              className="fas fa-star text-warning ms-2"
              title={intl.formatMessage({ id: 'quickSale.hasPriority' })}
            />
          )}
          <Link
            to={`/user/${userId}/plan/${plan.id}`}
            className="d-block fs-8 link fw-normal"
            title={intl.formatMessage({ id: 'quickSale.otherDetails' })}
          >
            {plan.code}
          </Link>
        </td>
        <td>
          <select
            className={clsx(
              'form-select form-select-sm',
              planDraft.kind && 'border-primary'
            )}
            value={kind}
            disabled={!editable}
            aria-label={intl.formatMessage({ id: 'quickSale.planKind' })}
            onChange={(event) => onKindChange(event.target.value as any)}
          >
            <option value="automatic">
              {intl.formatMessage({ id: 'quickSale.automatic' })}
            </option>
            <option value="custom">
              {intl.formatMessage({ id: 'quickSale.custom' })}
            </option>
          </select>
          {kind === 'custom' && editable && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0 mt-1"
              onClick={() => onCustomEditorOpenChange(true)}
            >
              {intl.formatMessage({ id: 'quickSale.editRecipes' })}
            </button>
          )}
        </td>
        <td>
          <select
            className={clsx(
              'form-select form-select-sm',
              planDraft.period && 'border-primary'
            )}
            value={periodId ?? ''}
            disabled={!editable}
            aria-label={intl.formatMessage({ id: 'quickSale.periodicity' })}
            onChange={(event) => onPeriodChange(Number(event.target.value))}
          >
            {plan.available_periods.map((period) => (
              <option key={period.id} value={period.id}>
                {intl.formatMessage(
                  { id: 'quickSale.days' },
                  { days: period.period }
                )}
              </option>
            ))}
          </select>
        </td>
        <td>
          <select
            className={clsx(
              'form-select form-select-sm',
              planDraft.combine !== undefined && 'border-primary'
            )}
            value={combine ? 'yes' : 'no'}
            disabled={!editable || !plan.can_combine}
            aria-label={intl.formatMessage({ id: 'quickSale.complementary' })}
            onChange={(event) => onCombineChange(event.target.value === 'yes')}
          >
            <option value="no">
              {intl.formatMessage({ id: 'quickSale.no' })}
            </option>
            <option value="yes">
              {intl.formatMessage({ id: 'quickSale.yes' })}
            </option>
          </select>
        </td>
        <td>
          <div className="d-flex align-items-center gap-2">
            <PriceBreakdown price={viewPlan?.price ?? plan.price} />
            {simulating && (
              <span className="spinner-border spinner-border-sm text-primary" />
            )}
          </div>
          {editable && (
            <button
              type="button"
              className="btn btn-link btn-sm p-0"
              onClick={() => setVariantsOpen(true)}
            >
              {intl.formatMessage({ id: 'quickSale.seeVariants' })}
            </button>
          )}
        </td>
        <td>
          {plan.default_meal_gross_margin !== null
            ? `${intl.formatNumber(plan.default_meal_gross_margin * 100, {
                maximumFractionDigits: 1,
              })}%`
            : '-'}
        </td>
        <td>{formatEuro(intl, viewPlan?.shipping_fee ?? plan.shipping_fee)}</td>
        <td>
          <StateBadge state={plan.state} kind="plan" />
        </td>
      </tr>
    );
  }

  function renderRow(plan: QuickSalePlanModel) {
    return (
      <tr
        key={plan.id}
        className="cursor-pointer"
        onClick={() => onSelectPlan(plan.id)}
      >
        <td className="ps-4 fw-bold">
          {pet.name}
          <span className="d-block fs-8 text-muted">{plan.code}</span>
        </td>
        <td>
          {intl.formatMessage({
            id:
              plan.kind === 'custom'
                ? 'quickSale.custom'
                : 'quickSale.automatic',
          })}
        </td>
        <td>
          {plan.period
            ? intl.formatMessage(
                { id: 'quickSale.days' },
                { days: plan.period.period }
              )
            : '-'}
        </td>
        <td>
          {intl.formatMessage({
            id: plan.combine ? 'quickSale.yes' : 'quickSale.no',
          })}
        </td>
        <td>
          <PriceBreakdown price={plan.price} />
        </td>
        <td>
          {plan.default_meal_gross_margin !== null
            ? `${intl.formatNumber(plan.default_meal_gross_margin * 100, {
                maximumFractionDigits: 1,
              })}%`
            : '-'}
        </td>
        <td>{formatEuro(intl, plan.shipping_fee)}</td>
        <td>
          <StateBadge state={plan.state} kind="plan" />
        </td>
      </tr>
    );
  }

  return (
    <QuickSaleCard
      icon="fa-layer-group"
      title={intl.formatMessage({ id: 'quickSale.plans' })}
      actions={
        <>
          <select
            className="form-select form-select-sm w-auto"
            value={filter}
            aria-label={intl.formatMessage({ id: 'quickSale.filterByState' })}
            onChange={(event) => onFilterChange(event.target.value)}
          >
            <option value={CURRENT_FILTER}>
              {intl.formatMessage({ id: 'quickSale.currentPlans' })}
            </option>
            <option value={ALL_FILTER}>
              {intl.formatMessage({ id: 'quickSale.allStates' })}
            </option>
            {states.map((state) => (
              <option key={state} value={state}>
                {intl.formatMessage({ id: `quickSalePlanState.${state}` })}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn btn-sm btn-primary"
            disabled={hasCurrentPlan || creatingPlan}
            title={
              hasCurrentPlan
                ? intl.formatMessage({ id: 'quickSale.alreadyHasPlan' })
                : undefined
            }
            onClick={onCreatePlan}
          >
            <i className="fas fa-plus me-1" />
            {intl.formatMessage({ id: 'quickSale.newPlan' })}
          </button>
        </>
      }
    >
      {draft.plan && (
        <div className="d-flex justify-content-end mb-2">
          <button
            type="button"
            className="btn btn-link btn-sm p-0"
            onClick={() => clear('plan')}
          >
            {intl.formatMessage({ id: 'quickSale.undoPlanChanges' })}
          </button>
        </div>
      )}
      <div className="table-responsive">
        <table className="table table-row-dashed table-row-gray-200 align-middle gs-0 gy-3 mb-0">
          <thead>
            <tr className="fw-bolder text-muted bg-light">
              <th className="ps-4 rounded-start">
                {intl.formatMessage({ id: 'quickSale.petName' })}
              </th>
              <th className="min-w-125px">
                {intl.formatMessage({ id: 'quickSale.planKind' })}
              </th>
              <th className="min-w-125px">
                {intl.formatMessage({ id: 'quickSale.periodicity' })}
              </th>
              <th className="min-w-100px">
                {intl.formatMessage({ id: 'quickSale.complementary' })}
              </th>
              <th>{intl.formatMessage({ id: 'quickSale.price' })}</th>
              <th>{intl.formatMessage({ id: 'quickSale.baseMargin' })}</th>
              <th>{intl.formatMessage({ id: 'quickSale.shipping' })}</th>
              <th className="rounded-end">
                {intl.formatMessage({ id: 'quickSale.state' })}
              </th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) =>
              plan.id === selectedPlan?.id
                ? renderSelectedRow(plan)
                : renderRow(plan)
            )}
            {!plans.length && (
              <tr>
                <td colSpan={8} className="text-center text-muted py-5">
                  {intl.formatMessage({ id: 'quickSale.noPlans' })}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {selectedPlan && customEditorOpen && (
        <PlanKindModal
          userId={userId}
          plan={selectedPlan}
          initialLines={planDraft.custom_lines}
          onClose={() => onCustomEditorOpenChange(false)}
          onApply={(lines, allow800gr) => {
            update('plan', {
              kind: 'custom',
              custom_lines: lines,
              allow_800gr: allow800gr || undefined,
            });
            onCustomEditorOpenChange(false);
          }}
        />
      )}
      {selectedPlan && variantsOpen && (
        <VariantsModal
          userId={userId}
          plan={selectedPlan}
          onClose={() => setVariantsOpen(false)}
          onApply={(planDraftValues) => {
            clear('plan');
            update('plan', planDraftValues || {});
            setVariantsOpen(false);
          }}
        />
      )}
    </QuickSaleCard>
  );
};

export { PlansCard };
